import { GetServerSideProps } from 'next';
import { captureMessage } from '@sentry/nextjs';

import { sendCounterMetric } from 'utils/metrics';
import { SanityArticleData } from 'lib/sanity/types';
import { getPreviewArticle, getPublishedArticle } from 'lib/sanity';
import { cleanSlug, isPreviewSecretValidForArticleSlug } from 'utils/url-helpers/url-helpers';
import Article from 'components/Article';
import { getSharedPageData } from 'utils/shared-page-data';
import createVariantResolver from 'utils/remote-config/content/create-variant-resolver';
import { CMSDataRequest } from 'types/sanity-request';
import { assertIsVertical } from 'utils/type-guards';
import { getVerticalConfig, Vertical } from 'utils/vertical';
import assemblePath from 'utils/assemblePath';
import getAlternateLinksByMarketIds from 'utils/i18n/alternateLink';
import { NotFoundError } from 'utils/errors';
import { serverExceptionHandler } from 'utils/exceptionHandler';

const getRedirectUrl = (slugInUrl: string, correctSlug: string, market: string, vertical: Vertical) => {
  if (slugInUrl === correctSlug) {
    return null;
  }

  const verticalPath = getVerticalConfig(vertical).pathParam;
  // If the slug is outdated, we redirect to the new one
  return assemblePath(market, verticalPath, 'article', correctSlug);
};

const getCMSData = async ({ articleSlug, market, isPreview, vertical }: CMSDataRequest): Promise<SanityArticleData> => {
  const slug = articleSlug.toLowerCase();

  let article;
  if (isPreview) {
    article = await getPreviewArticle({ slug, market, vertical });
  } else {
    article = await getPublishedArticle({ slug, market, vertical });

    // TODO: Temporary! Remove when internal links are fixed in S4P content, and all locales have some content for every article
    if (article && !article.content && vertical === Vertical.PODCASTERS) {
      captureMessage('Failed to get article for slug in preferred locale for market, falling back to en-US', {
        tags: {
          slug,
          market,
        },
      });
      article = await getPublishedArticle({ slug, market, vertical, localeOverride: 'en-US' });
    }
  }

  if (!article?.content) {
    throw new NotFoundError('404');
  }

  return article;
};

export const getServerSideProps: GetServerSideProps = async context => {
  const { req, query } = context;
  const market = query.market as string;
  const previewSecret = query.previewsecret as string;
  const variantKey = query.variant as string;
  let vertical: Vertical | undefined;

  try {
    const articleSlug = cleanSlug(query.articleslug as string);
    const isPreview =
      Boolean(previewSecret) && (await isPreviewSecretValidForArticleSlug(previewSecret, articleSlug.toLowerCase()));

    vertical = assertIsVertical(query.vertical);

    // GetSharedPageData takes a significant amount of time to resolve (due to remote config resolution)
    // To decrease request latency we want to run this in the background.
    const sharedPageDataPromise = getSharedPageData(context);

    const resolveVariant = createVariantResolver(vertical, articleSlug, {
      userId: req.headers['sp-internal-user-id'] as string | undefined,
      installationId: req.headers['sp-internal-tracking-id'] as string,
      isPreview,
      variantKey,
      req,
    });

    const { masthead: mastheadData, ...article } = await getCMSData({ articleSlug, market, isPreview, vertical });

    const redirectUrl = getRedirectUrl(articleSlug, article.slugs[0], market, vertical);
    if (redirectUrl) {
      sendCounterMetric('article-redirect', { redirect: 'redirect', vertical });
      return {
        redirect: { destination: redirectUrl, permanent: false },
      };
    }
    sendCounterMetric('article-redirect', { redirect: 'no-redirect', vertical });

    const resolvedArticle = await resolveVariant(article);

    const sharedPageData = await sharedPageDataPromise;

    const alternateLinks = getAlternateLinksByMarketIds(vertical, article.availableMarketIds, `article/${articleSlug}`);

    return {
      props: {
        article: resolvedArticle,
        articleSlug,
        mastheadData,
        sharedPageData,
        alternateLinks,
      },
    };
  } catch (error) {
    return serverExceptionHandler(error, 'article-page', { market, vertical });
  }
};

export default Article;
